import { MessageDescriptor } from 'react-intl'

import { ProgramNames, PROVIDER_TYPES } from '../../models'
import { PROGRAM_TAXONOMY, ProgramCustomerPropertyId } from '../common/constants/customerProgram'
import { TREATMENT_OPTIONS_TYPE as TreatmentOptionsType } from '../common/constants/treatmentOptions'

export type ProgramConfig = {
  /** Name of the program, usually the name of the program from LyraTherapy */
  programName: ProgramNames

  /** Customer property ID used to enable/disable this program for a customer */
  customerPropertyId: ProgramCustomerPropertyId

  /** Program ID From LyraTherapy, or null if not a Lyra Care program */
  blendedCareProgramId: string | null

  /** Bit flags for this program
   * To assign multiple flags, use the bitwise OR operator: `|`
   * @example `flags: ProgramFlags.isCoaching | ProgramFlags.eligibleForSelfPay`
   * @see ProgramFlags
   */
  flags: bigint

  /** Configuration for linking program to triage treatment options and taxonomy */
  treatmentOptionProperties?: {
    /** List of triage treatment options associated with this program */
    associatedTriageTreatmentOptions: TreatmentOptionsType[]
    /** Provider program taxonomy or CTPO options */
    programTaxonomyTreatmentOptions?: ProgramTaxonomyType[]
    /** Whether this is a full treatment option or a subset */
    isFullTreatmentOption: boolean
  }

  /** Provider `lyra_type`s associated with this program
   *
   * @deprecated The `lyra_type` provider property is deprecated and should not be used for new logic.
   */
  associatedProviderTypes: ProviderLyraType[]

  /** Associated provider `modalities` field from provider profile data */
  associatedProviderModalities: string[]
  // NOTE: Only one known usage: getProviderProgramsFromProviderModalities in lyrahealthwebapp,
  // consider deprecating if it's no longer needed

  /** Whether appointments for this program can be rescheduled */
  canRescheduleAppointment: boolean

  /** Consent agreements required for this program */
  consents: Consent[]

  /** Question identifiers for suicidal ideation and self-harm screening across different assessments */
  questionIdentifiers?: {
    SI_QUESTION?: string
    SI_PLAN_QUESTION?: string
    SI_INTENT_QUESTION?: string
    CHECK_IN_SI_QUESTION?: string
    CHECK_IN_SELF_HARM_QUESTION?: string
    CHRT_SI_QUESTION?: string
    CHRT_SI_QUESTION_2?: string
    CHRT_SI_PLAN_QUESTION?: string
    THOUGHTS_ABOUT_DEATH_QUESTION?: string
    CLIENT_INTAKE_ATTEMPTED_SUICIDE_QUESTION?: string
    CLIENT_INTAKE_SELF_INJURY_QUESTION?: string
  }
}

/**
 * Flags that can be used to attach properties or opt-in behaviors to a program.
 * They are represented as bitwise flags to describe multiple boolean properties of a program in a single value.
 * (BigInt is used to support more than 32 flags.)
 *
 * - Use the `Programs.programHasAllFlags` helpers to check for a program's flags.
 * - To set or check for multiple flags, use the bitwise OR operator: `|`
 *
 * @example const flags = ProgramFlags.isCoaching | ProgramFlags.eligibleForSelfPay
 */
export const ProgramFlags = {
  /** No flags set */
  none: 0n,

  /** Is a coaching program */
  isCoaching: 1n << 0n,
  /** Is a therapy program */
  isTherapy: 1n << 1n,
  /** Is a Direct Access program on the Engage platform */
  isDirectAccessEngage: 1n << 2n,
  /** Is a program for minors (17 and under) only */
  forMinors: 1n << 3n,
  /** Is eligible for self-pay when EAP sessions are exhausted */
  eligibleForSelfPay: 1n << 4n,

  // TODO: Check if this is needed and if it fits usage of `const ousPrograms` in customerProgram.tsx
  // supportsInternational: 1n << 5n,
} as const

export type ProgramFlagsType = (typeof ProgramFlags)[keyof typeof ProgramFlags]

export type ProgramTaxonomyType = {
  clientele: (typeof PROGRAM_TAXONOMY.clientele)[keyof typeof PROGRAM_TAXONOMY.clientele]
  treatment: (typeof PROGRAM_TAXONOMY.treatment)[keyof typeof PROGRAM_TAXONOMY.treatment]
  partner: (typeof PROGRAM_TAXONOMY.partner)[keyof typeof PROGRAM_TAXONOMY.partner]
  offering: (typeof PROGRAM_TAXONOMY.offering)[keyof typeof PROGRAM_TAXONOMY.offering]
}

export type ProviderLyraType = (typeof PROVIDER_TYPES)[keyof typeof PROVIDER_TYPES]['lyraType']

export type Consent = {
  consentText: MessageDescriptor
  consentUrl: string
  showConsentIf: (options?: { isInternational?: boolean }) => boolean
}
